const ResponsibleGaming = () => {
  return (
    <main className="min-h-screen py-20 bg-dark-black">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Responsible Gaming
          </h1>
          <p className="text-gray-400 mb-8">
            Your well-being is our priority
          </p>
          
          <div className="prose prose-invert max-w-none space-y-6 text-gray-300 leading-relaxed">
            <p className="text-xl text-gray-200">
              At Empire Wager, we are committed to promoting responsible gambling and ensuring that our website provides information and resources to help you gamble safely. We recognize that while gambling can be entertaining, it can also become problematic for some individuals. This page provides information about responsible gambling and resources to help you maintain control.
            </p>

            <h2 className="text-3xl font-bold text-white mt-12 mb-6">What is Responsible Gambling?</h2>
            <p>
              Responsible gambling means gambling in a way that is safe, controlled, and does not cause harm to yourself or others. It involves understanding the risks, setting limits, and knowing when to stop. Responsible gambling is about making informed decisions and maintaining control over your gambling activities.
            </p>
            <p>
              Gambling should be treated as entertainment, not as a way to make money or solve financial problems. It's important to only gamble with money you can afford to lose and to never chase losses. Responsible gambling means gambling within your means and recognizing when gambling stops being fun.
            </p>

            <h2 className="text-3xl font-bold text-white mt-12 mb-6">Signs of Problem Gambling</h2>
            <p>
              Problem gambling can affect anyone, regardless of age, income, or background. It's important to recognize the warning signs early so you can take action. Common signs of problem gambling include:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Spending more money or time gambling than you can afford</li>
              <li>Chasing losses by gambling more to try to win back money</li>
              <li>Lying to family or friends about gambling activities</li>
              <li>Neglecting work, school, or family responsibilities because of gambling</li>
              <li>Borrowing money or selling possessions to fund gambling</li>
              <li>Feeling anxious, depressed, or irritable when not gambling</li>
              <li>Gambling to escape problems or relieve feelings of stress or depression</li>
              <li>Needing to gamble with increasing amounts of money to feel excitement</li>
              <li>Repeated unsuccessful efforts to control, cut back, or stop gambling</li>
            </ul>
            <p>
              If you recognize these signs in yourself or someone you know, it's important to seek help. Problem gambling is treatable, and there are many resources available to provide support.
            </p>

            <h2 className="text-3xl font-bold text-white mt-12 mb-6">How to Gamble Responsibly</h2>
            <p>
              There are several steps you can take to ensure you gamble responsibly:
            </p>
            
            <h3 className="text-2xl font-bold text-white mt-8 mb-4">Set Limits</h3>
            <p>
              Before you start gambling, decide how much money and time you can afford to spend. Set clear limits and stick to them. Never gamble with money you need for essential expenses like rent, bills, or food. Only gamble with disposable income that you can afford to lose.
            </p>
            
            <h3 className="text-2xl font-bold text-white mt-8 mb-4">Never Chase Losses</h3>
            <p>
              Chasing losses by gambling more to try to win back money is one of the most dangerous gambling behaviors. Losses are part of gambling, and trying to recover them usually leads to bigger losses. Accept losses as part of the experience and walk away when you've reached your limits.
            </p>
            
            <h3 className="text-2xl font-bold text-white mt-8 mb-4">Take Regular Breaks</h3>
            <p>
              Don't gamble for extended periods without breaks. Regular breaks help you maintain perspective and make better decisions. Set time limits for your gambling sessions and stick to them.
            </p>
            
            <h3 className="text-2xl font-bold text-white mt-8 mb-4">Don't Gamble When Emotional</h3>
            <p>
              Avoid gambling when you're feeling stressed, depressed, angry, or anxious. These emotions can cloud your judgment and lead to poor decisions. Only gamble when you're in a clear state of mind.
            </p>
            
            <h3 className="text-2xl font-bold text-white mt-8 mb-4">Understand the Odds</h3>
            <p>
              Remember that gambling is designed to favor the house. The odds are always against you in the long run. Understanding this reality helps you maintain realistic expectations and avoid chasing unrealistic wins.
            </p>
            
            <h3 className="text-2xl font-bold text-white mt-8 mb-4">Keep It Fun</h3>
            <p>
              Gambling should be entertaining, not stressful or problematic. If gambling stops being fun or starts causing problems in your life, it's time to stop. Don't continue gambling if it's no longer enjoyable.
            </p>

            <h2 className="text-3xl font-bold text-white mt-12 mb-6">Responsible Gambling Tools</h2>
            <p>
              Most UK-licensed online casinos offer responsible gambling tools to help you maintain control. These tools include:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Deposit Limits:</strong> Set maximum amounts you can deposit daily, weekly, or monthly</li>
              <li><strong>Loss Limits:</strong> Automatically stop your session when losses reach a predetermined amount</li>
              <li><strong>Session Time Limits:</strong> Set maximum time limits for gambling sessions</li>
              <li><strong>Wager Limits:</strong> Set maximum bet amounts per spin or hand</li>
              <li><strong>Reality Checks:</strong> Pop-up reminders showing how long you've been playing and how much you've spent</li>
              <li><strong>Self-Exclusion:</strong> Ban yourself from a casino for a set period or permanently</li>
            </ul>
            <p>
              We encourage you to use these tools proactively, before you need them. Set limits when you're thinking clearly, not during emotional moments. These tools are designed to help you maintain control and gamble responsibly.
            </p>

            <h2 className="text-3xl font-bold text-white mt-12 mb-6">GAMSTOP</h2>
            <p>
              GAMSTOP is a free service that allows you to self-exclude from all UK-licensed gambling operators simultaneously. If you feel you need to take a break from gambling, GAMSTOP can help you exclude yourself from all UK casinos, betting sites, and other gambling operators for a period of 6 months, 1 year, 2 years, or 5 years.
            </p>
            <p>
              Once you've registered with GAMSTOP, you cannot reverse your exclusion during the exclusion period. This prevents impulsive decisions to resume gambling. GAMSTOP is a powerful tool for players who need to take a complete break from gambling.
            </p>
            <p>
              To register with GAMSTOP, visit <a href="https://www.gamstop.co.uk" target="_blank" rel="noopener noreferrer" className="text-toxic-orange hover:text-orange-400">www.gamstop.co.uk</a>. The service is free, confidential, and available to all UK residents.
            </p>

            <h2 className="text-3xl font-bold text-white mt-12 mb-6">Getting Help</h2>
            <p>
              If you're struggling with problem gambling, help is available. There are many organizations that provide free, confidential support:
            </p>
            
            <h3 className="text-2xl font-bold text-white mt-8 mb-4">BeGambleAware</h3>
            <p>
              BeGambleAware provides information, advice, and support for anyone affected by problem gambling. They operate the National Gambling Helpline and provide resources for understanding problem gambling and finding help.
            </p>
            <p>
              <strong>Website:</strong> <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer" className="text-toxic-orange hover:text-orange-400">www.begambleaware.org</a><br />
              <strong>Helpline:</strong> 0808 8020 133 (free and confidential, available 24/7)
            </p>
            
            <h3 className="text-2xl font-bold text-white mt-8 mb-4">GamCare</h3>
            <p>
              GamCare offers free support, advice, and counseling for anyone affected by problem gambling. They provide online chat, phone support, and face-to-face counseling services.
            </p>
            <p>
              <strong>Website:</strong> <a href="https://www.gamcare.org.uk" target="_blank" rel="noopener noreferrer" className="text-toxic-orange hover:text-orange-400">www.gamcare.org.uk</a><br />
              <strong>Helpline:</strong> 0808 8020 133<br />
              <strong>Live Chat:</strong> Available on their website
            </p>
            
            <h3 className="text-2xl font-bold text-white mt-8 mb-4">GAMSTOP</h3>
            <p>
              GAMSTOP allows you to self-exclude from all UK-licensed gambling operators. This free service helps you take a break from gambling by preventing access to all UK gambling sites.
            </p>
            <p>
              <strong>Website:</strong> <a href="https://www.gamstop.co.uk" target="_blank" rel="noopener noreferrer" className="text-toxic-orange hover:text-orange-400">www.gamstop.co.uk</a>
            </p>
            
            <h3 className="text-2xl font-bold text-white mt-8 mb-4">NHS Services</h3>
            <p>
              Your GP can refer you to specialist gambling treatment services. Problem gambling is a recognized mental health condition, and NHS services are available. Don't hesitate to seek medical help if gambling is affecting your mental health or daily life.
            </p>

            <h2 className="text-3xl font-bold text-white mt-12 mb-6">For Friends and Family</h2>
            <p>
              If someone you know is struggling with problem gambling, there are resources available to help you support them:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>BeGambleAware provides support for friends and family affected by someone else's gambling</li>
              <li>GamCare offers counseling and support services for families</li>
              <li>Many local support groups provide peer support for families affected by problem gambling</li>
            </ul>
            <p>
              Remember that you cannot force someone to stop gambling, but you can encourage them to seek help and provide support when they're ready to make changes.
            </p>

            <h2 className="text-3xl font-bold text-white mt-12 mb-6">Underage Gambling</h2>
            <p>
              Gambling is illegal for anyone under the age of 18 in the UK. We do not knowingly allow individuals under 18 to use our website or access gambling services through links on our site.
            </p>
            <p>
              If you are under 18, you must not use our website or attempt to access gambling services. If you know someone under 18 who is gambling, please report it to the relevant authorities or support organizations.
            </p>
            <p>
              Parents and guardians should be aware of the risks of underage gambling and take steps to prevent children from accessing gambling websites. This includes using parental controls, monitoring internet usage, and educating children about the risks of gambling.
            </p>

            <h2 className="text-3xl font-bold text-white mt-12 mb-6">Our Commitment</h2>
            <p>
              Empire Wager is committed to promoting responsible gambling. We provide information about responsible gambling practices, links to support organizations, and resources to help you gamble safely. We only link to UK-licensed operators that are required to provide responsible gambling tools and support.
            </p>
            <p>
              However, we cannot control how you use the information on our website or how you gamble at third-party casinos. Ultimately, responsible gambling is your responsibility. We encourage you to use the resources and tools available to maintain control and gamble safely.
            </p>

            <h2 className="text-3xl font-bold text-white mt-12 mb-6">Contact Us</h2>
            <p>
              If you have questions about responsible gambling or need information about support resources, please contact us:
            </p>
            <p>
              <strong>Email:</strong> info@empire-wagers.com<br />
              <strong>Phone:</strong> +44 5511 778 569<br />
              <strong>Address:</strong> London, United Kingdom
            </p>
            <p>
              Remember: If you're struggling with problem gambling, please contact one of the support organizations listed above. They provide professional, confidential help and support.
            </p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ResponsibleGaming;

